<template>
  <Teleport to="body">
    <div v-if="isVisible"
         class="modal-backdrop"
    >
      <section class="contact-modal"
               role="dialog"
               aria-modal="true"
               :aria-labelledby="titleId"
      >
        <div class="modal-heading">
          <span class="material-icons">contact_phone</span>
          <div>
            <h2 :id="titleId">{{ t('accountPage.contactPrompt.title') }}</h2>
            <p>{{ t('accountPage.contactPrompt.description') }}</p>
          </div>
        </div>

        <form @submit.prevent="savePhone">
          <MainInput :text="phone"
                     type="text"
                     name="contact-phone"
                     :placeholder="t('accountPage.phone')"
                     @value-changed="phone = $event.target.value"
          />

          <div class="modal-actions">
            <button type="button"
                    class="secondary-button"
                    @click="closePrompt"
            >
              {{ t('accountPage.contactPrompt.later') }}
            </button>
            <button type="submit"
                    class="primary-button"
                    :disabled="isSaving"
            >
              {{ isSaving ? t('accountPage.saving') : t('accountPage.contactPrompt.save') }}
            </button>
          </div>
        </form>
      </section>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import { useToast } from 'vue-toastification'
import MainInput from '@/components/inputs/MainInput.vue'
import i18n from '@/locales/i18n'

const store = useStore()
const toast = useToast()
const t = i18n.global.t
const titleId = 'contact-information-prompt-title'

const phone = ref('')
const isSaving = ref(false)
const isVisible = computed(() => store.state.account.contactInformationPromptVisible)
const languagesByLocale = {
  sv: 'Swedish',
  en: 'English',
  no: 'Norwegian',
  dk: 'Danish'
}

function closePrompt () {
  store.commit('account/hideContactInformationPrompt')
}

async function savePhone () {
  const trimmedPhone = phone.value.trim()
  if (!trimmedPhone) {
    toast.warning(t('accountPage.contactPrompt.phoneRequired'))
    return
  }

  isSaving.value = true
  const user = store.state.account.me
  try {
    await store.dispatch('account/updateMe', {
      Name: user.Name || '',
      Email: user.Email || '',
      Language: languagesByLocale[store.state.account.language] || user.Language || 'Swedish',
      Phone: trimmedPhone
    })
    closePrompt()
    toast.success(t('accountPage.contactPrompt.saveSuccess'))
  } catch (error) {
    toast.error(t('accountPage.contactPrompt.saveFailed'))
  } finally {
    isSaving.value = false
  }
}
</script>

<style lang="scss" scoped>
.modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(15, 23, 42, 0.65);
}

.contact-modal {
  width: min(480px, 100%);
  padding: 2rem;
  color: white;
  background: var(--mainBlue);
  border-radius: 20px;
  box-shadow: 0 20px 45px rgba(0, 0, 0, 0.3);
}

.modal-heading {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.5rem;

  .material-icons {
    font-size: 2.5rem;
    color: var(--mainLightBlue);
  }

  h2 {
    margin-bottom: 0.35rem;
    font-size: 1.35rem;
  }

  p {
    line-height: 1.4;
    color: var(--mainLightBlue);
  }
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 0.5rem;

  button {
    min-width: 110px;
    padding: 0.75rem 1rem;
    font-weight: 600;
    border: 2px solid white;
    border-radius: 2rem;
  }
}

.secondary-button {
  color: white;
}

.primary-button {
  color: var(--mainBlue);
  background: white;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }
}

@media (max-width: 480px) {
  .contact-modal {
    padding: 1.25rem;
  }

  .modal-actions {
    flex-direction: column-reverse;

    button {
      width: 100%;
    }
  }
}
</style>
